import {
  normalizer,
  get,
  getRequest,
  getWithMeta
} from 'helpers/RequestUtil'
import {
  select,
  fork,
  put,
  all,
  call,
  take,
  takeLatest,
  takeEvery,
  cancelled,
} from 'redux-saga/effects'
import {
  baseURL
} from 'config'

import {
  fetchReport as fetchReportAction,
  fetchReportStart,
  fetchReportSuccess,
  fetchReportError,
  updateReport,
  removeReport,
  clearReports,
  updateQuerySource,
  resetQuery as resetQueryAction,
  clearFilter
} from './actions'

import {
  MOUNT,
  FETCH_REPORT,
  ON_SELECT_FILTER,
  ON_UNSELECT_FILTER,
  UPDATE_QUERY_CHANGES,
  RESET_QUERY,
  ProductHierarchyEnum,
  PartnerSiteEnum,
  chartsEnum,
  AeRegionEnum,
} from './constants'

import {
  querySelector,
  queryChangesSelector,
  projectCountsQuerySelector,
  aeResourcesQuerySelector,
  aeRegionsQuerySelector
} from './selectors'
import { update } from 'lodash'


function* fetchReport(report, moreQuery) {
  try {
    yield put(fetchReportStart())

    // main query
    const querySelection = yield select(querySelector)
    const combinedFilters = { ...PartnerSiteEnum, ...ProductHierarchyEnum, ...AeRegionEnum }
    const filters = Object.values(combinedFilters)
    let query = []
    filters.map(val => {
      const selection = querySelection(val)
      if (selection.length != 0) query.push(`${val.filter}=${selection.map(sel => sel.value).join()}`)
    })
    // chart specific queries
    if (report === chartsEnum.TOTAL_COUNTS.value) {
      const projectCountSelection = yield select(projectCountsQuerySelector)
      query.push(`lifecycle=${projectCountSelection.join()}`)
    } else if (report === chartsEnum.SPECIFICATION.value) {
      query.push(`parent=${moreQuery ? moreQuery.parent : chartsEnum.SPECIFICATION.default.parent}`)
      query.push(`value=${moreQuery ? moreQuery.value : chartsEnum.SPECIFICATION.default.value}`)
    } else if (report === chartsEnum.AE_REGIONS.value) {
      const aeRegionsSelection = yield select(aeRegionsQuerySelector)
      query.push(`region=${aeRegionsSelection.join('//')}`)
    }

    const data = yield call(getRequest, `${baseURL}/api/fulcrum/dashboard/${report}?${query.join('&')}`)
    yield put(fetchReportSuccess())
    return data
  } catch (error) {
    yield put(fetchReportError(error))
  }
}

function* getPartnerSiteFilters() {
  const queryChanges = yield select(queryChangesSelector)
  const partnerSiteChanges = queryChanges(Object.values(PartnerSiteEnum)[0])
  return partnerSiteChanges.map(change => change.id)
}

function* findDeepestParent(level) {
  const queryChanges = yield select(queryChangesSelector)
  const levelIndex = Object.values(ProductHierarchyEnum).findIndex(other => level.label === other.label)
  const parentLevels = Object.values(ProductHierarchyEnum).filter((level, index) => index < levelIndex)
  const filterLevel = parentLevels.reverse().find((level) => queryChanges(level) && queryChanges(level).length > 0)
  return filterLevel
}

function* updateHierarchyLevelSource(level) {
  const queryChanges = yield select(queryChangesSelector)
  const parent = yield call(findDeepestParent, level)
  if (parent) {
    const depth = level.value - parent.value
    const changes = queryChanges(parent)
    const data = yield all(changes.map(change => call(fetchHierarchySource, depth, change.id)))
    const results = data.reduce((result, value) => {
      return {
        ...result,
        ...value
      }
    }, {})
    yield put(updateQuerySource(level, results))
  } else {
    yield call(updateHierarchySource, level)
  }
}

function* updateHierarchyChangeSource() {
  const promises = Object.values(ProductHierarchyEnum).map(level => call(updateHierarchyLevelSource, level))
  yield all(promises)
}

function* fetchHierarchySource(depth, id = 1) {
  const partner_site_ids = yield call(getPartnerSiteFilters)
  let url = `/api/fulcrum/product_hierarchy_views/${id}/children?filter[depth]=${depth}`
  if (partner_site_ids && partner_site_ids.length > 0) {
    url += `&filter[partner_site_id]=${partner_site_ids.join(',')}`
  }
  const data = yield call(get, url)
  return data.entities.product_children_views
}

function* updateHierarchySource(level) {
  const data = yield call(fetchHierarchySource, level.value)
  yield put(updateQuerySource(level, data))
}

function* updatePartnerSiteLevel(partnerSiteLevel, sources) {
  const property = partnerSiteLevel.source
  const results = sources.reduce((result, newVal) => {
    const name = newVal[property]
    const id = newVal.id
    return {
      ...result,
      [name]: {
        name,
        id
      }
    }
  }, {})
  yield put(updateQuerySource(partnerSiteLevel, results))
}

function* updatePartnerSites() {
  const partnerSitesLevels = Object.values(PartnerSiteEnum)
  const queryChanges = yield select(queryChangesSelector)
  const hierarchy_ids = Object.values(ProductHierarchyEnum).reduce((result, level) => {
    const level_ids = queryChanges(level).map(sel => sel.id)
    return [
      ...result,
      ...level_ids
    ]
  }, [])

  let url = '/api/fulcrum/dashboard/partner_site_views'
  if (hierarchy_ids && hierarchy_ids.length > 0) {
    url += `?filter[product_hierarchy_id]=${hierarchy_ids.join(',')}`
  }
  const siteData = yield call(get, url)
  const data = Object.values(siteData.entities.partner_site_views || siteData.entities)
  yield all(partnerSitesLevels.map(level => call(updatePartnerSiteLevel, level, data)))
}

function* updateAeRegions() {
  let url = '/api/fulcrum/ae_regions_selection'
  const regions = Object.values(AeRegionEnum)
  const siteData = yield call(get, url)
  const data = Object.values(siteData.entities.ae_regions_selections)[0]['aeRegions']
  yield all(regions.map(region => put(updateQuerySource(region, data))))
}


function* updateQuerySourceByChange(action) {
  const { name } = action
  if (Object.values(ProductHierarchyEnum).some(level => level.label === name)) {
    yield call(updateHierarchyChangeSource)
    yield call(updatePartnerSites)
  } else {
    yield call(updateHierarchyChangeSource)
  }
}

function* updateQuerySources() {
  const hierachyLevels = Object.values(ProductHierarchyEnum)
  let promises = hierachyLevels.map(level => call(updateHierarchySource, level))
  promises = promises.concat(call(updatePartnerSites))
  yield all(promises)
}

function* resetQuery() {
  yield call(updateQuerySources)
}

function* handleSelectFilter(action) {
  const data = yield call(handleFetchReport, action)
}

function* handleUnselectFilter(action) {
  const { report } = action
  yield put(removeReport(report))
}

function* updateAeResourceChart(report, segment) {
  try {
    yield put(fetchReportStart())
    const data = yield call(getRequest, `${baseURL}/api/fulcrum/dashboard/${report}?segment=${segment}`)
    yield put(fetchReportSuccess())
    yield put(updateReport(data))
  } catch (error) {
    yield put(fetchReportError(error))
  }
}

function* handleFetchReport(action) {
  const { report, query } = action
  if (report == chartsEnum.AE_RESOURCES.value) {  // as ae_resources chart is not affected by main query
    const aeResourcesSelection = yield select(aeResourcesQuerySelector)
    let promises = aeResourcesSelection.map(segment => call(updateAeResourceChart, report, segment))
    yield all(promises)
  } else {
    const data = yield call(fetchReport, report, query)
    yield put(updateReport(data))
  }
}

function* handleMount() {
  yield put(clearFilter())
  yield put(resetQueryAction())
  yield put(clearReports())
  yield call(updateQuerySources)
  yield call(updateAeRegions)
  yield put(fetchReportAction(chartsEnum.TOTAL_COUNTS.value, `&lifecycle=${['qualification', 'design_review'].join()}`))
}

function* watchFetchReport() {
  yield takeEvery(FETCH_REPORT, handleFetchReport)
}

function* watchSelectFilter() {
  yield takeEvery(ON_SELECT_FILTER, handleSelectFilter)
}

function* watchUnselectFilter() {
  yield takeEvery(ON_UNSELECT_FILTER, handleUnselectFilter)
}

function* watchQueryChange() {
  yield takeLatest(UPDATE_QUERY_CHANGES, updateQuerySourceByChange)
}

function* watchResetQuery() {
  yield takeLatest(RESET_QUERY, resetQuery)
}

function* watchMount() {
  yield takeEvery(MOUNT, handleMount)
}

function* loadSagas() {
  yield all([
    fork(watchFetchReport),
    fork(watchSelectFilter),
    fork(watchUnselectFilter),
    fork(watchQueryChange),
    fork(watchResetQuery),
  ])
}

export {
  watchMount
}

export default loadSagas